// Usage Rights Details Card — structured card showing usage rights terms for a deliverable

import { formatDurationLabel } from "./content-status-badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Separator,
} from "@repo/ui";
import { format, addDays } from "date-fns";

// ============================================================================
// Types
// ============================================================================

interface UsageRightsDetailsCardProps {
  /** Usage rights duration in days (-2 = perpetual, -1/0/null = none, positive = days) */
  usageRightsDuration: number | null | undefined;
  /** Date the content was approved (Unix timestamp in seconds, or ISO string) */
  dateApproved: string | number | null | undefined;
  /** Date the content was first uploaded/submitted (Unix timestamp in seconds, or ISO string) */
  dateFirstUpload: string | number | null | undefined;
  /** Whether the deliverable requires content approval (GCR) */
  requiresApproval: boolean;
  /** Custom duration value from agreement (e.g. 2 for "2 months") */
  customValue?: number | null;
  /** Custom duration unit from agreement ("days", "months", "years") */
  customUnit?: string | null;
  /**
   * Whether the brief grants content usage rights at all. Briefs that record
   * rights as free-text legal language (e.g. Managed/custom briefs) set this
   * to true without ever populating the structured `usageRightsDuration` enum.
   * In that case the rights ARE granted even though the duration is unset, so
   * we must not display "not granted" (EPD-9453).
   */
  requireContentRights?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function normalizeToDate(
  date: number | string | null | undefined,
): Date | null {
  if (!date) return null;

  let dateObj: Date;
  if (typeof date === "number") {
    // Timestamps >= 1e12 are milliseconds; smaller values are seconds
    const timestampMs = date >= 1e12 ? date : date * 1000;
    dateObj = new Date(timestampMs);
  } else {
    dateObj = new Date(date);
  }

  return isNaN(dateObj.getTime()) ? null : dateObj;
}

function formatDate(date: number | string | null | undefined): string | null {
  const dateObj = normalizeToDate(date);
  return dateObj ? format(dateObj, "MMMM d, yyyy") : null;
}

// ============================================================================
// Sub-components
// ============================================================================

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function UsageRightsDetailsCard({
  usageRightsDuration,
  dateApproved,
  dateFirstUpload,
  requiresApproval,
  customValue,
  customUnit,
  requireContentRights,
}: UsageRightsDetailsCardProps) {
  const isPerpetual = usageRightsDuration === -2;
  const hasTimeBound = usageRightsDuration != null && usageRightsDuration > 0;

  // Format display dates
  const formattedApprovedDate = formatDate(dateApproved);
  const formattedSubmittedDate = formatDate(dateFirstUpload);

  // Calculate expiration for time-bound variants
  let formattedExpiration: string | null = null;
  let durationLabel: string | null = null;

  if (hasTimeBound) {
    durationLabel = formatDurationLabel(
      usageRightsDuration,
      customValue,
      customUnit,
    );

    const rawStartDate = requiresApproval ? dateApproved : dateFirstUpload;
    const startDateObj = normalizeToDate(rawStartDate);

    if (startDateObj) {
      const expirationDate = addDays(startDateObj, usageRightsDuration);
      formattedExpiration = format(expirationDate, "MMMM d, yyyy");
    }
  }

  // Duration suffix for time-bound variants
  const durationSuffix = requiresApproval ? "from approval" : "from submission";

  // Footer message. `null` when the rights status is genuinely unknown — no
  // structured duration AND `requireContentRights` is undefined (e.g. the brief
  // enhancements query errored/hasn't resolved). In that case we render no
  // footer rather than falsely claiming "not granted" (EPD-9453). "Not granted"
  // is shown only when we positively know rights are not required.
  let footerMessage: string | null;
  if (isPerpetual) {
    footerMessage =
      "The brand has a perpetual license to use this content with no expiration.";
  } else if (hasTimeBound && formattedExpiration) {
    footerMessage = `After ${formattedExpiration}, the brand is required to stop using this content.`;
  } else if (hasTimeBound) {
    footerMessage = "The brand has time-limited usage rights for this content.";
  } else if (requireContentRights === true) {
    // Rights granted via free-text legal language without a structured duration.
    footerMessage =
      "The brand has usage rights for this content as described in the signed brief.";
  } else if (requireContentRights === false) {
    footerMessage =
      "The brand has not been granted usage rights for this content.";
  } else {
    footerMessage = null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Approved on — only when approval is required and date exists */}
        {requiresApproval && formattedApprovedDate && (
          <DetailRow label="Approved on" value={formattedApprovedDate} />
        )}

        {/* Submitted on */}
        {formattedSubmittedDate && (
          <DetailRow label="Submitted on" value={formattedSubmittedDate} />
        )}

        {/* Usage Rights Duration — perpetual */}
        {isPerpetual && (
          <DetailRow label="Usage Rights Duration" value="Perpetual" />
        )}

        {/* Usage Rights Duration — time-bound */}
        {hasTimeBound && durationLabel && (
          <DetailRow
            label="Usage Rights Duration"
            value={`${durationLabel} ${durationSuffix}`}
          />
        )}

        {/* Valid through — time-bound with calculated date */}
        {hasTimeBound && formattedExpiration && (
          <DetailRow label="Valid through" value={formattedExpiration} />
        )}
      </CardContent>
      {footerMessage && (
        <>
          <Separator />
          <CardFooter className="pt-4">
            <p className="text-sm text-muted-foreground">{footerMessage}</p>
          </CardFooter>
        </>
      )}
    </Card>
  );
}
